import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // ➜ inicializace parent odkazů (Tenisky, Nike, Air Jordan, …)
    this.#initParentLinks();

    // reagujeme na otevření/zavření <details>
    this.addEventListener('toggle', this.#onDetailsToggle, true);

    // speciální chování při kliknutí na text názvu kategorie
    this.addEventListener('click', this.#onParentLinkClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
    this.removeEventListener('click', this.#onParentLinkClick, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll(
          'details[open]:not(accordion-custom > details)',
        );
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener(
          'animationend',
          removeWillChangeOnAnimationEnd,
        );
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Uloží URL z menu-drawer__parent-link do data-url a
   * u zavřených <details> odstraní href (první klik jen otevře).
   */
  #initParentLinks() {
    const detailsElements = this.querySelectorAll('details');
    detailsElements.forEach((details) => {
      const summaryLink = details.querySelector(
        'summary .menu-drawer__parent-link',
      );
      if (!summaryLink) return;

      const existingUrl =
        summaryLink.getAttribute('href') ||
        summaryLink.getAttribute('data-url');

      if (!existingUrl) return;

      summaryLink.setAttribute('data-url', existingUrl);

      if (details.open) {
        summaryLink.setAttribute('href', existingUrl);
      } else {
        summaryLink.removeAttribute('href');
      }
    });
  }

  /**
   * Při otevření/zavření details vrací/odebírá href na názvu kategorie.
   */
  #onDetailsToggle = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLDetailsElement)) return;

    const details = target;
    const summaryLink = details.querySelector(
      'summary .menu-drawer__parent-link',
    );
    if (!summaryLink) return;

    const url = summaryLink.getAttribute('data-url');
    if (!url) return;

    if (details.open) {
      // submenu je otevřené → klik na text naviguje
      summaryLink.setAttribute('href', url);
    } else {
      // submenu je zavřené → klik na text jen otevře
      summaryLink.removeAttribute('href');
    }
  };

  /**
   * Když je submenu otevřené a klikneš na text názvu,
   * nechceme znovu přepínat <details> – jen navigovat.
   */
  #onParentLinkClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const parentLink = target.closest('.menu-drawer__parent-link');
    if (!parentLink) return;

    const details = parentLink.closest('details');
    if (!(details instanceof HTMLDetailsElement)) return;

    if (details.open) {
      // submenu je otevřené → blokneme toggle <details>
      event.stopPropagation();
    }
    // pokud je zavřené, href stejně není → klik jen otevře details
  };
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
